import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import type { Action } from '@/config/permissions';

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
  /**
   * Permission required to access this route.
   * If not specified, only authentication is required.
   */
  requires?: Action;

  /**
   * Redirect path for unauthenticated users
   * @default '/login'
   */
  redirectTo?: string;

  /**
   * Redirect path when the user lacks the permission
   * @default '/'
   */
  unauthorizedRedirectTo?: string;
}

/**
 * Loading spinner component shown during auth check
 */
function LoadingSpinner() {
  return (
    <div className="loading-container">
      <div className="loading-spinner" />
      <p>Loading...</p>
    </div>
  );
}

/**
 * ProtectedRoute component that guards routes based on authentication and permissions
 *
 * @example
 * ```tsx
 * // Require only authentication
 * <Route element={<ProtectedRoute />}>
 *   <Route path="dashboard" element={<Dashboard />} />
 * </Route>
 *
 * // Require a permission
 * <Route element={<ProtectedRoute requires="users:manage" />}>
 *   <Route path="users" element={<Users />} />
 * </Route>
 * ```
 */
export function ProtectedRoute({
  requires,
  redirectTo = '/login',
  unauthorizedRedirectTo = '/',
}: ProtectedRouteProps) {
  const { isAuthenticated, isInitializing } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();

  // Show loading spinner while restoring the session
  if (isInitializing) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location so we can redirect back after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  if (requires && !can(requires)) {
    return <Navigate to={unauthorizedRedirectTo} replace />;
  }

  // User is authenticated and authorized, render the protected content
  return <Outlet />;
}
